import { useEffect } from 'react';
import { APPEARANCE_APPLIED_EVENT } from '@/lib/app-settings';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { electronInvoke } from '@/lib/electron-bridge';
import { nativeDesktopPlatform } from '@/lib/native-desktop';

/** Left inset (px) reserved for the macOS window traffic lights when not fullscreen. */
const MAC_TITLE_BAR_INSET = 92;

/**
 * The CSS variable (on the root element) carrying the width the Windows and Linux window
 * controls take at the title bar's right end; the title bar keeps its own controls clear of it.
 */
const WINDOW_CONTROLS_INSET_PROPERTY = '--window-controls-inset';

/** Chromium's Window Controls Overlay API, which TypeScript's DOM types lack. */
type WindowControlsOverlay = EventTarget & {
  readonly visible: boolean;
  getTitlebarAreaRect(): DOMRect;
};

/** A CSS color (`oklch(…)` included) as `#rrggbb`, as the window controls take it. */
function hexColor(context: CanvasRenderingContext2D, color: string): string {
  context.clearRect(0, 0, 1, 1);
  context.fillStyle = color;
  context.fillRect(0, 0, 1, 1);
  const [red = 0, green = 0, blue = 0] = context.getImageData(0, 0, 1, 1).data;
  return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Measures the window buttons' gutters: the macOS traffic lights' (platform + fullscreen
 * aware) into the store, so the title bar and full-window pages can clear them; on Windows
 * and Linux, the window controls overlay's into `--window-controls-inset`, whose colors
 * follow the content's under them (the window has no title bar) through the theme changes.
 */
export function useTitleBarInsets({
  store,
  native,
  preview,
}: {
  store: DesktopShellStore;
  native: boolean | null;
  preview: boolean;
}): void {
  useEffect(() => {
    if (native !== true) return;
    const fullscreen = window.matchMedia('(display-mode: fullscreen)');
    const updateInset = (): void => {
      const macOS = preview ? /Mac/.test(navigator.platform) : nativeDesktopPlatform() === 'macos';
      const isFullscreen = fullscreen.matches || document.fullscreenElement !== null;
      store.getState().setMacTitleBarInset(macOS && !isFullscreen ? MAC_TITLE_BAR_INSET : 0);
    };
    updateInset();
    fullscreen.addEventListener('change', updateInset);
    document.addEventListener('fullscreenchange', updateInset);
    return () => {
      fullscreen.removeEventListener('change', updateInset);
      document.removeEventListener('fullscreenchange', updateInset);
    };
  }, [store, native, preview]);

  useEffect(() => {
    if (native !== true || preview) return;
    const platform = nativeDesktopPlatform();
    if (platform === null || platform === 'macos') return;
    const overlay = (navigator as Navigator & { windowControlsOverlay?: WindowControlsOverlay })
      .windowControlsOverlay;
    const root = document.documentElement;
    const updateInset = (): void => {
      // The title bar area ends where the controls begin; hidden (fullscreen), they take none.
      const inset = overlay?.visible
        ? Math.max(0, window.innerWidth - overlay.getTitlebarAreaRect().right)
        : 0;
      root.style.setProperty(WINDOW_CONTROLS_INSET_PROPERTY, `${inset}px`);
    };
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    let reportedColors = '';
    const reportColors = (): void => {
      if (context === null) return;
      const style = getComputedStyle(document.body);
      const colors = {
        // The controls sit over the content's top right (the conversation header, the panel's tabs).
        color: hexColor(context, style.getPropertyValue('--background').trim()),
        symbolColor: hexColor(context, style.getPropertyValue('--foreground').trim()),
      };
      const serialized = JSON.stringify(colors);
      if (serialized === reportedColors) return;
      reportedColors = serialized;
      void electronInvoke('window_title_bar_colors', colors).catch(() => {
        reportedColors = '';
      });
    };
    updateInset();
    reportColors();
    overlay?.addEventListener('geometrychange', updateInset);
    window.addEventListener('resize', updateInset);
    window.addEventListener(APPEARANCE_APPLIED_EVENT, reportColors);
    return () => {
      overlay?.removeEventListener('geometrychange', updateInset);
      window.removeEventListener('resize', updateInset);
      window.removeEventListener(APPEARANCE_APPLIED_EVENT, reportColors);
      root.style.removeProperty(WINDOW_CONTROLS_INSET_PROPERTY);
    };
  }, [native, preview]);
}
